import React, { useState, useEffect, useRef } from 'react';
import { crearItemMenu, actualizarItemMenu } from '../../services/productosApi';
import { obtenerCategorias } from '../../services/categoriasServicio';
import { obtenerInsumos } from '../../services/insumosServicio';
import { formatCLP } from '../Utiles/Formatters';
import config from '../../config/apiConfig';
import TemplateManagerModal from './TemplateManagerModal';
const generarIdTemporal = () => Date.now();

// ===========================================
//  1. ICONOS
// ===========================================
const CloseIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>);
const AddIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>);
const TrashIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>);
const SpinnerIcon = () => (<span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>);

// ===========================================
//  1.5 CONFIRM MODAL (reemplaza alert/confirm/prompt)
// ===========================================
const ConfirmModal = ({ isVisible, title, message, inputLabel, inputValue, type, onConfirm, onCancel }) => {
    const [localValue, setLocalValue] = useState(inputValue || '');

    useEffect(() => {
        if (isVisible) setLocalValue(inputValue || '');
    }, [isVisible, inputValue]);

    if (!isVisible) return null;

    const isPrompt = type === 'prompt';
    const isAlert = type === 'alert';

    return (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 9999 }}>
            <div className="modal-dialog modal-dialog-centered modal-sm">
                <div className="modal-content shadow-lg border-0" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                    <div className={`modal-header border-0 pb-0 ${isAlert ? 'bg-info' : 'bg-warning'}`}>
                        <h5 className="modal-title font-weight-bold text-white">
                            {isAlert ? 'ℹ️ Información' : isPrompt ? '✏️ Entrada requerida' : '⚠️ Confirmación'}
                        </h5>
                        <button type="button" className="close text-white" onClick={onCancel}><CloseIcon /></button>
                    </div>
                    <div className="modal-body p-4 text-center">
                        <p className="mb-0 font-weight-bold" style={{ fontSize: '1.1rem' }}>{message}</p>
                        {isPrompt && (
                            <div className="mt-3">
                                <label className="font-weight-bold small text-muted">{inputLabel || 'Valor'}</label>
                                <input
                                    type="text"
                                    className="form-control form-control-lg text-center font-weight-bold border-primary"
                                    value={localValue}
                                    onChange={(e) => setLocalValue(e.target.value)}
                                    autoFocus
                                />
                            </div>
                        )}
                    </div>
                    <div className="modal-footer border-0 pt-0 d-flex justify-content-center" style={{ gap: '10px' }}>
                        {!isAlert && (
                            <button type="button" className="btn btn-outline-secondary px-4 rounded-pill" onClick={onCancel}>
                                Cancelar
                            </button>
                        )}
                        <button
                            type="button"
                            className={`btn px-4 rounded-pill font-weight-bold text-white ${isAlert ? 'btn-info' : 'btn-warning'}`}
                            onClick={() => onConfirm(isPrompt ? localValue : true)}
                        >
                            {isAlert ? 'Entendido' : isPrompt ? 'Aceptar' : 'Sí, continuar'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ===========================================
//  2. MODAL TERCIARIO: AddOptionModal (también usado para editar)
// ===========================================
const AddOptionModal = ({ isVisible, onClose, onSave, groupName, editData }) => {
    const [nombreOpcion, setNombreOpcion] = useState('');
    const [ajustePrecio, setAjustePrecio] = useState(0);
    const [esDefault, setEsDefault] = useState(false);

    // Cuando se abre en modo edición, precargar datos
    useEffect(() => {
        if (isVisible && editData) {
            setNombreOpcion(editData.nombre_opcion || editData.nombre || '');
            setAjustePrecio(editData.ajuste_precio || 0);
            setEsDefault(editData.seleccionado_por_defecto === true || editData.seleccionado_por_defecto === 1);
        } else if (isVisible && !editData) {
            // Modo crear: limpiar
            setNombreOpcion('');
            setAjustePrecio(0);
            setEsDefault(false);
        }
    }, [isVisible, editData]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!nombreOpcion) return;
        onSave({
            nombre_opcion: nombreOpcion,
            ajuste_precio: Number(ajustePrecio),
            seleccionado_por_defecto: esDefault
        });
    };

    if (!isVisible) return null;
    const esEdicion = !!editData;
    return (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1070 }}>
            <div className="modal-dialog modal-dialog-centered modal-sm">
                <div className="modal-content shadow-lg">
                    <div className="modal-header bg-success text-white">
                        <h5 className="modal-title font-weight-bold">{esEdicion ? 'Editar Opción' : 'Añadir Opción'}</h5>
                        <button type="button" className="close text-white" onClick={onClose}><CloseIcon /></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <div className="form-group">
                                <label className="font-weight-bold">Nombre</label>
                                <input type="text" value={nombreOpcion} onChange={(e) => setNombreOpcion(e.target.value)} className="form-control" required placeholder="Ej: Palta, Queso" />
                            </div>
                            <div className="form-group">
                                <label className="font-weight-bold">Ajuste de Precio (CLP)</label>
                                <input type="number" value={ajustePrecio} onChange={(e) => setAjustePrecio(e.target.value)} className="form-control" min="0" />
                            </div>

                            <div className="form-check mt-3 bg-light p-2 rounded border">
                                <input
                                    type="checkbox"
                                    className="form-check-input ml-1"
                                    id="checkDefault"
                                    checked={esDefault}
                                    onChange={(e) => setEsDefault(e.target.checked)}
                                />
                                <label className="form-check-label ml-4 font-weight-bold text-dark" htmlFor="checkDefault" style={{ cursor: 'pointer' }}>
                                    Marcado por defecto
                                </label>
                                <small className="d-block text-muted ml-4">Aparecerá seleccionado en el TPV</small>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
                            <button type="submit" className="btn btn-success font-weight-bold">{esEdicion ? 'Actualizar' : 'Guardar'}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
// ===========================================
//  3. MODAL SECUNDARIO: AddGroupModal
// ===========================================
const AddGroupModal = ({ isVisible, onClose, onSaveGroup }) => {
    const [nombreGrupo, setNombreGrupo] = useState('');
    const [tipoSeleccion, setTipoSeleccion] = useState('Única (Obligatoria)');
    const [maxSeleccion, setMaxSeleccion] = useState(0);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!nombreGrupo) return;

        onSaveGroup({
            nombre_grupo: nombreGrupo,
            tipo_seleccion: tipoSeleccion,
            max_seleccion: Number(maxSeleccion),
            orden: 0, // Se manda 0 por defecto, ya que lo ordenas afuera
            opciones: [],
            db_id: null
        });

        onClose();
        setNombreGrupo('');
        setMaxSeleccion(0);
    };

    if (!isVisible) return null;
    return (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered modal-sm">
                <div className="modal-content shadow-lg border-0">
                    <div className="modal-header bg-info text-white">
                        <h5 className="modal-title font-weight-bold">Configurar Grupo</h5>
                        <button type="button" className="close text-white" onClick={onClose}><CloseIcon /></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body p-4">
                            <div className="form-group mb-3">
                                <label className="font-weight-bold small mb-1">Nombre (Ej: Salsas)</label>
                                <input type="text" value={nombreGrupo} onChange={(e) => setNombreGrupo(e.target.value)} className="form-control" required placeholder="Ej: Proteínas" autoFocus />
                            </div>
                            <div className="form-group mb-3">
                                <label className="font-weight-bold small mb-1">Tipo de Selección</label>
                                <select value={tipoSeleccion} onChange={(e) => setTipoSeleccion(e.target.value)} className="form-control">
                                    <option value="Única (Obligatoria)">Única (Obligatoria)</option>
                                    <option value="Única (Opcional)">Única (Opcional)</option>
                                    <option value="Múltiple (Opcional)">Múltiple (Opcional)</option>
                                </select>
                            </div>

                            {/* SOLO MOSTRAR MAX ELEGIR SI ES MÚLTIPLE */}
                            {tipoSeleccion.includes('Múltiple') && (
                                <div className="form-group bg-light p-2 rounded border">
                                    <label className="font-weight-bold small text-danger mb-1">Máx. ítems a elegir</label>
                                    <input type="number" value={maxSeleccion} onChange={(e) => setMaxSeleccion(e.target.value)} className="form-control border-danger" min="0" placeholder="0 = Sin límite" />
                                </div>
                            )}
                        </div>
                        <div className="modal-footer bg-light">
                            <button type="submit" className="btn btn-info btn-block font-weight-bold text-white shadow-sm">
                                <i className="fas fa-plus-circle mr-1"></i> CREAR GRUPO
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// ===========================================
//  4. COMPONENTE: VariantManager
// ===========================================
const VariantManager = ({ productVariants, setProductVariants, openAddGroupModal, setIsTemplateModalOpen, onSaveAsTemplate, showConfirm }) => {
    const [isOptionModalOpen, setIsOptionModalOpen] = useState(false);
    const [currentGroupIndex, setCurrentGroupIndex] = useState(null);
    const [editOptionData, setEditOptionData] = useState(null); // <-- NUEVO: datos de la opción a editar
    const [editOptionIndex, setEditOptionIndex] = useState(null); // <-- NUEVO: índice de la opción a editar

    const moveGroup = (index, direction) => {
        const newVariants = [...productVariants];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        // Si intenta subir el primero o bajar el último, no hacemos nada
        if (targetIndex < 0 || targetIndex >= newVariants.length) return;

        // Intercambiamos los grupos
        const temp = newVariants[index];
        newVariants[index] = newVariants[targetIndex];
        newVariants[targetIndex] = temp;

        // Re-asignamos los números de orden basados en su nueva posición
        const finalVariants = newVariants.map((grupo, i) => ({
            ...grupo,
            orden: i + 1
        }));

        setProductVariants(finalVariants);
    };
    const handleUpdateGroupName = (index, nuevoNombre) => {
        const nuevasVariantes = productVariants.map((g, i) =>
            i === index ? { ...g, nombre_grupo: nuevoNombre } : g
        );
        setProductVariants(nuevasVariantes);
    };
    const handleUpdateGroupType = (index, nuevoTipo) => {
        const nuevasVariantes = productVariants.map((g, i) =>
            i === index ? { ...g, tipo_seleccion: nuevoTipo } : g
        );
        setProductVariants(nuevasVariantes);
    };
    const handleAddOptionClick = (groupIndex) => {
        setCurrentGroupIndex(groupIndex);
        setEditOptionData(null); // <-- Modo crear
        setEditOptionIndex(null);
        setIsOptionModalOpen(true);
    };

    const handleSaveNewOption = (newOptionData) => {
        if (currentGroupIndex === null) return;
        const opcionFinal = { ...newOptionData, id: 'temp_' + Date.now() };
        const newVariants = productVariants.map((group, index) => {
            if (index === currentGroupIndex) {
                return { ...group, opciones: [...(group.opciones || []), opcionFinal] };
            }
            return group;
        });
        setProductVariants(newVariants);
        setIsOptionModalOpen(false);
        setCurrentGroupIndex(null);
    };

    // <-- NUEVO: Abrir modal para editar opción existente
    const handleEditOptionClick = (groupIndex, optionIndex) => {
        const opcion = productVariants[groupIndex].opciones[optionIndex];
        setCurrentGroupIndex(groupIndex);
        setEditOptionIndex(optionIndex);
        setEditOptionData(opcion);
        setIsOptionModalOpen(true);
    };

    // <-- NUEVO: Guardar cambios de una opción editada
    const handleSaveEditedOption = (newOptionData) => {
        if (currentGroupIndex === null || editOptionIndex === null) return;
        const newVariants = productVariants.map((group, index) => {
            if (index === currentGroupIndex) {
                const nuevasOpciones = group.opciones.map((opt, i) => {
                    if (i === editOptionIndex) {
                        return { ...opt, ...newOptionData };
                    }
                    return opt;
                });
                return { ...group, opciones: nuevasOpciones };
            }
            return group;
        });
        setProductVariants(newVariants);
        setIsOptionModalOpen(false);
        setCurrentGroupIndex(null);
        setEditOptionIndex(null);
        setEditOptionData(null);
    };

    const handleDeleteOption = (groupIndex, optionIndex) => {
        showConfirm({
            title: 'Eliminar opción',
            message: '¿Estás seguro de eliminar esta opción?',
            type: 'confirm',
            onConfirm: () => {
                const newVariants = productVariants.map((group, index) => {
                    if (index === groupIndex) {
                        return { ...group, opciones: group.opciones.filter((_, i) => i !== optionIndex) };
                    }
                    return group;
                });
                setProductVariants(newVariants);
            }
        });
    };

    const handleDeleteGroup = (groupIndex) => {
        const nombre = productVariants[groupIndex].nombre_grupo;
        showConfirm({
            title: 'Eliminar grupo',
            message: `¿Eliminar grupo "${nombre}"?`,
            type: 'confirm',
            onConfirm: () => {
                setProductVariants(productVariants.filter((_, index) => index !== groupIndex));
            }
        });
    };

    return (
        <div className="p-3 bg-light border rounded mt-3 shadow-sm">
            <h6 className="font-weight-bold mb-3 text-dark border-bottom pb-2">
                <i className="fas fa-sliders-h mr-2"></i> Variantes del Menú
            </h6>

            {productVariants.length === 0 && (
                <div className="alert alert-info small text-center">No hay variantes. ¡Carga una o crea una!</div>
            )}

            {productVariants.map((grupo, index) => (
                <div key={index} className="mb-3 p-3 border rounded bg-white shadow-sm border-left-primary" style={{ borderLeftWidth: '5px' }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center">
                            {/* BOTONES DE ORDENAMIENTO VISUAL */}
                            <div className="btn-group-vertical mr-3">
                                <button
                                    type="button"
                                    className="btn btn-xs btn-outline-secondary p-0 px-1"
                                    onClick={() => moveGroup(index, 'up')}
                                    disabled={index === 0}
                                    style={{ fontSize: '10px' }}
                                >
                                    <i className="fas fa-chevron-up"></i>
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-xs btn-outline-secondary p-0 px-1"
                                    onClick={() => moveGroup(index, 'down')}
                                    disabled={index === productVariants.length - 1}
                                    style={{ fontSize: '10px' }}
                                >
                                    <i className="fas fa-chevron-down"></i>
                                </button>
                            </div>

                            <input
                                type="text"
                                value={grupo.nombre_grupo}
                                onChange={(e) => handleUpdateGroupName(index, e.target.value)}
                                className="form-control form-control-sm font-weight-bold text-primary border-0 bg-transparent p-0"
                                style={{ fontSize: '1.1rem', boxShadow: 'none', width: 'auto' }}
                            />
                        </div>
                        <select
                            value={grupo.tipo_seleccion}
                            onChange={(e) => handleUpdateGroupType(index, e.target.value)}
                            className="form-control form-control-sm font-weight-bold text-white bg-info border-0"
                            style={{ width: 'auto', fontSize: '0.75rem', cursor: 'pointer' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <option value="Única (Obligatoria)">Única (Obligatoria)</option>
                            <option value="Única (Opcional)">Única (Opcional)</option>
                            <option value="Múltiple (Opcional)">Múltiple (Opcional)</option>
                        </select>
                    </div>
                    {/* ... resto del código (opciones, etc) ... */}
                    {/* ... el resto sigue igual ... */}
                    {/* MOSTRAR EL MÁXIMO SI EXISTE */}
                    {grupo.max_seleccion > 0 && (
                        <small className="text-danger font-weight-bold d-block mb-2">
                            <i className="fas fa-exclamation-circle mr-1"></i>
                            Máximo permitido: {grupo.max_seleccion} ítems
                        </small>
                    )}

                    <ul className="list-group list-group-flush small mb-2">
                        {grupo.opciones.map((opcion, optionIndex) => {
                            const esMarcado = opcion.seleccionado_por_defecto == 1 || opcion.seleccionado_por_defecto === true;
                            return (
                                <li key={opcion.id} className={`list-group-item d-flex justify-content-between align-items-center px-0 py-1 border-0 ${esMarcado ? 'bg-light font-weight-bold' : ''}`}>
                                    <span>
                                        {esMarcado && <span className="mr-2">✅</span>}
                                        {opcion.nombre_opcion || opcion.nombre}
                                    </span>
                                    <div className="d-flex align-items-center">
                                        <span className="text-muted mr-3">{opcion.ajuste_precio > 0 ? `+$${opcion.ajuste_precio.toLocaleString()}` : 'Incluido'}</span>
                                        {/* BOTÓN EDITAR OPCIÓN */}
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light p-0 text-info border-0 mr-2"
                                            onClick={() => handleEditOptionClick(index, optionIndex)}
                                            title="Editar opción"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        </button>
                                        <button type="button" className="btn btn-sm btn-light p-0 text-danger border-0" onClick={() => handleDeleteOption(index, optionIndex)}>
                                            <TrashIcon />
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>

                    {/* BOTÓN PARA AÑADIR OPCIÓN (ESTO ES LO QUE TE FALTABA) */}
                    <button
                        type="button"
                        className="btn btn-sm btn-block btn-outline-success border-0 py-1 mb-2"
                        style={{ backgroundColor: '#f8fff9', borderStyle: 'dashed !important', borderWidth: '1px' }}
                        onClick={() => handleAddOptionClick(index)}
                    >
                        <i className="fas fa-plus-circle mr-1"></i> Añadir Opción a este grupo
                    </button>

                    <div className="d-flex justify-content-between mt-2 border-top pt-2">
                        <button
                            type="button"
                            className={`btn btn-sm btn-link p-0 font-weight-bold ${grupo.plantilla_id ? 'text-warning' : 'text-info'}`}
                            onClick={() => onSaveAsTemplate(grupo)}
                        >
                            {grupo.plantilla_id ? (
                                <>
                                    <i className="fas fa-sync-alt mr-1"></i> Actualizar Maestro
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-save mr-1"></i> Guardar Plantilla
                                </>
                            )}
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-danger border-0" onClick={() => handleDeleteGroup(index)}>
                            <TrashIcon /> Eliminar Grupo
                        </button>
                    </div>
                </div>
            ))}

            <div className="d-flex mt-3 shadow-sm" style={{ gap: '10px' }}>
                <button type="button" className="btn btn-outline-info flex-grow-1 font-weight-bold" onClick={() => setIsTemplateModalOpen(true)}>
                    <i className="fas fa-copy mr-1"></i> Cargar Plantilla
                </button>
                <button type="button" className="btn btn-primary flex-grow-1 font-weight-bold" onClick={openAddGroupModal}>
                    <AddIcon /> Nuevo Grupo
                </button>
            </div>

            <AddOptionModal
                isVisible={isOptionModalOpen}
                onClose={() => {
                    setIsOptionModalOpen(false);
                    setEditOptionData(null);
                    setEditOptionIndex(null);
                }}
                onSave={editOptionData ? handleSaveEditedOption : handleSaveNewOption}
                groupName={currentGroupIndex !== null ? productVariants[currentGroupIndex].nombre_grupo : ''}
                editData={editOptionData}
            />
        </div>
    );
};
// ===========================================
//  COMPONENTE: RecipeManager (GESTOR DE RECETA)
// ===========================================
const UNIDAD_LABEL = { UN: 'un', KG: 'kg', G: 'g', L: 'l', ML: 'ml' };

const RecipeManager = ({ recipe, setRecipe, availableProducts }) => {
    const [selectedInsumo, setSelectedInsumo] = useState('');
    const [cantidad, setCantidad] = useState('');
    const [busqueda, setBusqueda] = useState('');
    const [showSugerencias, setShowSugerencias] = useState(false);
    const buscadorRef = useRef(null);

    const insumoSeleccionado = availableProducts.find(p => p.id === Number(selectedInsumo));
    const unidadSeleccionada = insumoSeleccionado ? (UNIDAD_LABEL[insumoSeleccionado.unidad_medida] || 'un') : null;

    const sugerencias = busqueda.length >= 1
        ? availableProducts.filter(p =>
            (p.nombre || p.name || '').toLowerCase().includes(busqueda.toLowerCase())
          ).slice(0, 12)
        : [];

    useEffect(() => {
        const handler = (e) => {
            if (buscadorRef.current && !buscadorRef.current.contains(e.target)) {
                setShowSugerencias(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const seleccionarInsumo = (p) => {
        setSelectedInsumo(String(p.id));
        setBusqueda(p.nombre || p.name);
        setShowSugerencias(false);
    };

    const handleAdd = () => {
        if (!selectedInsumo || !cantidad || cantidad <= 0) return;

        const productoRef = availableProducts.find(p => p.id === Number(selectedInsumo));
        if (!productoRef) return;

        const newItem = {
            id: productoRef.id,
            nombre: productoRef.nombre || productoRef.name,
            cantidad: Number(cantidad),
            unidad_medida: productoRef.unidad_medida || 'UN'
        };

        const existingItem = recipe.find(r => r.id === newItem.id);
        if (existingItem) {
            setRecipe(recipe.map(r => r.id === newItem.id ? { ...r, cantidad: r.cantidad + newItem.cantidad } : r));
        } else {
            setRecipe([...recipe, newItem]);
        }

        setSelectedInsumo('');
        setCantidad('');
        setBusqueda('');
    };

    const handleRemove = (id) => {
        setRecipe(recipe.filter(r => r.id !== id));
    };

    return (
        <div className="mt-4 p-3 border rounded bg-light">
            <h6 className="font-weight-bold text-info border-bottom pb-2">
                <i className="fas fa-blender mr-2"></i> Receta / Composición (Descuenta Insumos)
            </h6>

            <div className="form-row align-items-end mb-3 mt-3">
                <div className="col-md-7" ref={buscadorRef} style={{ position: 'relative' }}>
                    <label className="small font-weight-bold text-muted mb-1">Insumo / Producto</label>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Escribir nombre del insumo..."
                        value={busqueda}
                        onChange={(e) => {
                            setBusqueda(e.target.value);
                            setSelectedInsumo('');
                            setShowSugerencias(true);
                        }}
                        onFocus={() => setShowSugerencias(true)}
                        autoComplete="off"
                    />
                    {showSugerencias && sugerencias.length > 0 && (
                        <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0,
                            background: '#fff', border: '1px solid #ced4da',
                            borderRadius: '0 0 6px 6px', zIndex: 1000,
                            maxHeight: '220px', overflowY: 'auto',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                        }}>
                            {sugerencias.map(p => (
                                <div
                                    key={p.id}
                                    onMouseDown={() => seleccionarInsumo(p)}
                                    style={{
                                        padding: '7px 12px', cursor: 'pointer',
                                        borderBottom: '1px solid #f1f1f1',
                                        fontSize: '13px'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                >
                                    <strong>{p.nombre || p.name}</strong>
                                    <span style={{ color: '#888', marginLeft: '8px' }}>
                                        Stock: {parseFloat(p.stock || 0)} {UNIDAD_LABEL[p.unidad_medida] || 'un'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                    {showSugerencias && busqueda.length >= 1 && sugerencias.length === 0 && (
                        <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0,
                            background: '#fff', border: '1px solid #ced4da',
                            borderRadius: '0 0 6px 6px', zIndex: 1000,
                            padding: '8px 12px', fontSize: '13px', color: '#888'
                        }}>
                            Sin resultados
                        </div>
                    )}
                </div>
                <div className="col-md-3">
                    <label className="small font-weight-bold text-muted mb-1">Cantidad{unidadSeleccionada ? ` (${unidadSeleccionada})` : ''}</label>
                    <input type="number" className="form-control form-control-sm" step="0.001" min="0" value={cantidad} onChange={(e) => setCantidad(e.target.value)} placeholder="Ej: 0.5" />
                </div>
                <div className="col-md-2">
                    <button type="button" className="btn btn-sm btn-success btn-block" onClick={handleAdd}>Agregar</button>
                </div>
            </div>

            <ul className="list-group list-group-flush small border-top pt-2">
                {recipe.length === 0 && <li className="list-group-item text-muted text-center bg-transparent">Este producto no tiene receta armada.</li>}
                {recipe.map((item, idx) => (
                    <li key={idx} className="list-group-item d-flex justify-content-between align-items-center bg-transparent px-0 py-1">
                        <span><strong>{item.cantidad} {UNIDAD_LABEL[item.unidad_medida] || ''}</strong> de {item.nombre}</span>
                        <button type="button" className="btn btn-sm btn-light text-danger p-0 px-2" onClick={() => handleRemove(item.id)}><TrashIcon /></button>
                    </li>
                ))}
            </ul>
        </div>
    );
};

// ===========================================
//  5. COMPONENTE PRINCIPAL: ProductFormModal
// ===========================================
const ProductFormModal = ({ isVisible, onClose, onSave, productToEdit, allProducts = [] }) => {

    // 1. AÑADIMOS ESTE ESTADO NUEVO
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);

    const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
    const [categorias, setCategorias] = useState([]);
    const [insumosDisponibles, setInsumosDisponibles] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [saving, setSaving] = useState(false);

    // Estado del ConfirmModal unificado
    const [confirmState, setConfirmState] = useState({
        isVisible: false,
        title: '',
        message: '',
        type: 'confirm',
        inputLabel: '',
        inputValue: '',
        onConfirm: () => {},
        onCancel: () => {}
    });

    const showConfirm = ({ title, message, type = 'confirm', inputLabel = '', inputValue = '', onConfirm }) => {
        return new Promise((resolve) => {
            setConfirmState({
                isVisible: true,
                title,
                message,
                type,
                inputLabel,
                inputValue,
                onConfirm: (value) => {
                    setConfirmState(prev => ({ ...prev, isVisible: false }));
                    if (onConfirm) onConfirm(value);
                    resolve(value);
                },
                onCancel: () => {
                    setConfirmState(prev => ({ ...prev, isVisible: false }));
                    resolve(null);
                }
            });
        });
    };

    // ... (aquí sigue tu función getInitialState intacta) ...

    const getInitialState = (productData) => {
        // 1. Mapeo seguro de variantes para no perder el plantilla_id
        var initialVariants = [];
        if (productData && productData.variantes) {
            initialVariants = productData.variantes.map(function (item) {
                return {
                    ...item,
                    // Aseguramos que los nombres coincidan con lo que espera el Formulario
                    nombre_grupo: item.nombre_grupo || item.titulo || '',
                    tipo_seleccion: item.tipo_seleccion || item.tipo || 'Única (Obligatoria)',
                    plantilla_id: item.plantilla_id || null, // 🚩 VITAL: Aquí recuperamos el vínculo
                    opciones: item.opciones || []
                };
            });
        }

        if (productData) {
            let initialReceta = [];
            if (productData.receta_json) {
                try {
                    initialReceta = typeof productData.receta_json === 'string'
                        ? JSON.parse(productData.receta_json)
                        : productData.receta_json;
                } catch (e) {
                    console.error("Error al parsear receta_json:", e);
                    initialReceta = [];
                }
            }

            return {
                ...productData,
                name: productData.nombre || productData.name || '',
                description: productData.descripcion || productData.description || '',
                variantes: initialVariants, // 🚩 Usamos las variantes ya mapeadas
                price: formatCLP(productData.price || productData.precio),
                orden_visual: productData.orden_visual || 0,
                costo: productData.costo || '',
                codigo_barras: productData.codigo_barras || '',
                // Si ya tiene receta, no debe llevar stock propio (ver ProductFormModal RecipeManager):
                // su disponibilidad depende de los insumos, no de un contador aparte.
                controlar_stock: initialReceta.length > 0
                    ? false
                    : (productData.controlar_stock !== undefined ? Boolean(productData.controlar_stock) : false),
                stock: productData.stock || 0,
                stock_minimo: productData.stock_minimo || 0,
                category: productData.categoria_id || '',
                venta_por_peso: productData.venta_por_peso !== undefined ? Boolean(productData.venta_por_peso) : false,
                receta: initialReceta,
                precio_web: formatCLP(productData.precio_web || ''),
                venta_web: productData.venta_web !== undefined ? Boolean(productData.venta_web) : false,
                mostrar_tpv: productData.mostrar_tpv !== undefined ? Boolean(productData.mostrar_tpv) : true,
            };
        }

        // Estado inicial para producto nuevo
        return {
            name: '', price: '', category: '', description: '', img: '', stock: 0,
            variantes: [], // Empezamos vacío
            orden_visual: 0, costo: '', codigo_barras: '', controlar_stock: false,
            stock_minimo: 0, venta_por_peso: false, receta: [],
            precio_web: '', venta_web: false, categoria_uuid: '', mostrar_tpv: true
        };
    };
    const handleApplyTemplate = async (plantilla) => {
        const nombreOriginal = plantilla.nombre_plantilla || plantilla.nombre || "Nuevo Grupo";

        const nombrePersonalizado = await showConfirm({
            title: '✏️ Nombre del grupo',
            message: '¿Qué nombre quieres darle a este grupo para este producto?',
            type: 'prompt',
            inputLabel: 'Nombre del grupo',
            inputValue: nombreOriginal
        });

        if (!nombrePersonalizado) return;

        let opcionesFinales = [];
        try {
            if (plantilla.opciones_json) {
                opcionesFinales = typeof plantilla.opciones_json === 'string'
                    ? JSON.parse(plantilla.opciones_json)
                    : plantilla.opciones_json;
            }
        } catch (e) {
            console.error("Error al parsear opciones_json:", e);
        }

        if (!Array.isArray(opcionesFinales)) opcionesFinales = [];

        // 🚩 AQUÍ ESTÁ EL CAMBIO: Agregamos plantilla_id
        const nuevoGrupo = {
            nombre_grupo: nombrePersonalizado || nombreOriginal,
            tipo_seleccion: plantilla.tipo_seleccion || "Múltiple (Opcional)",
            max_seleccion: Number(plantilla.max_seleccion || 0),
            orden: Number(plantilla.orden || 0),

            plantilla_id: plantilla.id, // <--- 💡 ESTE ES EL VÍNCULO VITAL

            opciones: opcionesFinales.map(opt => ({
                id: 'temp_' + Date.now() + Math.random(),
                nombre_opcion: opt.nombre_opcion || opt.nombre || "Opción",
                ajuste_precio: Number(opt.ajuste_precio || opt.precio || 0),
                seleccionado_por_defecto: opt.seleccionado_por_defecto == 1 || opt.seleccionado_por_defecto === true
            })),
            db_id: null
        };

        setProduct(prev => ({
            ...prev,
            variantes: [...(prev.variantes || []), nuevoGrupo]
        }));

        setIsTemplateModalOpen(false);
    };

    // --- FUNCIÓN PARA GUARDAR UN GRUPO COMO PLANTILLA NUEVA ---
    const handleSaveAsTemplate = async (grupo) => {
        const token = localStorage.getItem('token');
        
        const opcionesLimpias = (grupo.opciones || []).map(opt => {
            const isDefault = opt.seleccionado_por_defecto === true || 
                              opt.seleccionado_por_defecto === "true" || 
                              opt.seleccionado_por_defecto == 1;

            return {
                id: opt.id || 'temp_' + Date.now() + Math.random(),
                nombre_opcion: opt.nombre_opcion || opt.nombre || "Opción",
                ajuste_precio: Number(opt.ajuste_precio || opt.precio || 0),
                seleccionado_por_defecto: isDefault
            };
        });

        // 1. SI ES UNA ACTUALIZACIÓN (Tiene plantilla_id)
        if (grupo.plantilla_id) {
            try {
                const resConteo = await fetch(`${config.api_url}/api/variantes-plantillas/${grupo.plantilla_id}/conteo`, {
                    headers: { 'token': token }
                });
                const dataConteo = await resConteo.json();
                const total = dataConteo.total || 0;

                const confirmado = await showConfirm({
                    title: 'Sincronizar Plantilla Maestra',
                    message: `Esta acción actualizará este grupo en los ${total} productos que lo utilizan. ¿Deseas continuar?`,
                    type: 'confirm'
                });

                if (confirmado) {
                    const res = await fetch(`${config.api_url}/api/variantes-plantillas/${grupo.plantilla_id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', 'token': token },
                        body: JSON.stringify({
                            nombre_plantilla: grupo.nombre_grupo,
                            tipo_seleccion: grupo.tipo_seleccion,
                            max_seleccion: grupo.max_seleccion,
                            opciones: opcionesLimpias,
                            propagar: true
                        })
                    });
                    
                    showConfirm({
                        title: res.ok ? '✅ Sincronización completada' : '❌ Error',
                        message: res.ok ? '¡Sincronización masiva completada!' : 'Hubo un problema al sincronizar. Revisa la red.',
                        type: 'alert'
                    });
                }
            } catch (e) { 
                showConfirm({ title: '❌ Error', message: 'Error al sincronizar: ' + e.message, type: 'alert' });
            }

        } else {
            // 2. SI ES UNA PLANTILLA NUEVA (No tiene plantilla_id)
            const nombre = await showConfirm({
                title: '✏️ Nombre de la plantilla',
                message: '¿Qué nombre quieres darle a esta plantilla?',
                type: 'prompt',
                inputLabel: 'Nombre de la plantilla',
                inputValue: grupo.nombre_grupo
            });
            if (!nombre) return;

            try {
                const res = await fetch(`${config.api_url}/api/variantes-plantillas`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'token': token },
                    body: JSON.stringify({
                        nombre_plantilla: nombre,
                        tipo_seleccion: grupo.tipo_seleccion,
                        max_seleccion: grupo.max_seleccion,
                        opciones: opcionesLimpias,
                        opciones_json: JSON.stringify(opcionesLimpias)
                    })
                });
                
                showConfirm({
                    title: res.ok ? '✅ Plantilla guardada' : '❌ Error',
                    message: res.ok ? 'Nueva plantilla guardada con éxito.' : 'Error del servidor al intentar guardar.',
                    type: 'alert'
                });
            } catch (e) { 
                showConfirm({ title: '❌ Error', message: 'Error al guardar: ' + e.message, type: 'alert' });
            }
        }
    };

    
   
    const [product, setProduct] = useState(getInitialState(productToEdit));
    const [formError, setFormError] = useState('');

    useEffect(() => {
        if (isVisible) {
            // 1. Cargamos el estado inicial (que ya trae el categoria_id en 'category')
            setProduct(getInitialState(productToEdit));

            const loadCategories = async () => {
                try {
                    const data = await obtenerCategorias();
                    setCategorias(data);

                    // 2. Si estamos editando, nos aseguramos de que el ID sea un número
                    if (productToEdit && productToEdit.categoria_id) {
                        setProduct(prev => ({
                            ...prev,
                            category: Number(productToEdit.categoria_id)
                        }));
                    }
                } catch (error) {
                    setFormError(`Error al cargar categorías: ${error.message}`);
                }
            };
            loadCategories();

            obtenerInsumos().then(setInsumosDisponibles).catch(() => setInsumosDisponibles([]));
        }
    }, [productToEdit, isVisible]);


    const handleVariantChange = (newVariants) => setProduct(prev => ({ ...prev, variantes: newVariants }));

    const handleAddGroupToProduct = (newGroup) => {
        setProduct(prev => ({
            ...prev,
            variantes: [...prev.variantes, { ...newGroup, id: generarIdTemporal(), db_id: null }]
        }));
        setIsGroupModalOpen(false);
    };

    const handleFileChange = (e) => {
        if (e.target.files.length > 0) setSelectedFile(e.target.files[0]);
        else setSelectedFile(null);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        setSaving(true);
        const token = localStorage.getItem('token');
        const formData = new FormData();
        const hayVariantes = product.variantes && product.variantes.length > 0;

        if (selectedFile) formData.append('image_file', selectedFile);
        if (!selectedFile && product.img) formData.append('url_imagen', product.img);

        formData.append('nombre', product.name);
        formData.append('precio', String(product.price).replace(/\D/g, ""));
        formData.append('categoria_id', String(product.category));
        formData.append('descripcion', product.description || '');
        formData.append('orden_visual', String(product.orden_visual || 0));
        formData.append('categoria_uuid', String(product.categoria_uuid || ''));

        formData.append('costo', String(product.costo).replace(/\D/g, "") || 0);
        formData.append('codigo_barras', product.codigo_barras || '');
        formData.append('controlar_stock', product.controlar_stock ? '1' : '0');
        formData.append('stock', String(product.stock || 0));
        formData.append('stock_minimo', String(product.stock_minimo || 0));
        formData.append('mostrar_tpv', product.mostrar_tpv ? '1' : '0');


        formData.append('variantes', JSON.stringify(product.variantes));
        formData.append('tiene_variantes', hayVariantes ? '1' : '0');
        formData.append('venta_por_peso', product.venta_por_peso ? '1' : '0');
        formData.append('receta_json', JSON.stringify(product.receta));
        formData.append('precio_web', String(product.precio_web).replace(/\D/g, "") || 0);
        formData.append('venta_web', product.venta_web ? '1' : '0');

        try {
            let productoId = productToEdit ? productToEdit.id : undefined;
            if (productoId) await actualizarItemMenu(productoId, formData);
            else await crearItemMenu(formData, token);

            onSave();
            onClose();
        } catch (error) {
            setFormError(`Fallo al guardar: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'price' || name === 'precio_web') {
            setProduct(prev => ({ ...prev, [name]: formatCLP(value) }));
        } else if (name === 'venta_web') {
            setProduct(prev => ({
                ...prev,
                venta_web: checked,
                // Si el usuario desmarca el check, reseteamos el precio web a vacío
                precio_web: checked ? prev.precio_web : ''
            }));
        } else {
            setProduct(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        }
    };

    if (!isVisible) return null;
    return (
        <div className="modal d-block mt-5" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto', zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">

                    <div className="modal-header bg-primary text-white">
                        <h5 className="modal-title">{productToEdit ? 'Editar Producto' : 'Nuevo Producto'}</h5>
                        <button type="button" className="close text-white" onClick={onClose}><CloseIcon /></button>
                    </div>

                    <div className="modal-body p-4">
                        {formError && <div className="alert alert-danger">{formError}</div>}
                        <div className="row">

                            {/* COLUMNA IZQUIERDA: FORMULARIO BASE + INVENTARIO */}
                            <div className="col-md-5 border-right pr-4">
                                <form id="product-base-form" onSubmit={handleFormSubmit}>

                                    <h6 className="font-weight-bold mb-3 border-bottom pb-2 text-primary">Información General</h6>

                                    {/* NOMBRE DEL PRODUCTO */}
                                    <div className="form-group">
                                        <label className="font-weight-bold">Nombre del Producto</label>
                                        <input type="text" name="name" value={product.name} onChange={handleChange} className="form-control" required />
                                    </div>

                                    <div className="form-row">
                                        {/* CATEGORÍA */}
                                        <div className="form-group col-md-6">
                                            <label className="font-weight-bold">Categoría</label>
                                            <select
                                                name="category" // 🚩 Cambiado de categoria_uuid a category
                                                value={product.category} // 🚩 Usa el ID numérico
                                                onChange={handleChange}
                                                className="form-control"
                                                required
                                            >
                                                <option value="">-- Seleccione --</option>
                                                {categorias.map(cat => (
                                                    <option key={cat.id} value={cat.id}>
                                                        {cat.nombre}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {/* ORDEN VISUAL */}
                                        <div className="form-group col-md-6">
                                            <label className="font-weight-bold text-muted">Orden en Catálogo</label>
                                            <input type="number" name="orden_visual" value={product.orden_visual} onChange={handleChange} className="form-control" min="0" placeholder="Ej: 1" />
                                        </div>
                                    </div>

                                    <div className="form-row bg-light p-3 rounded border mb-3">
                                        {/* PRECIO LOCAL */}
                                        <div className="form-group col-md-5 mb-0">
                                            <label className="font-weight-bold">Precio TPV / Local</label>
                                            <div className="input-group">
                                                <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                                                <input
                                                    type="text"
                                                    name="price"
                                                    value={product.price}
                                                    onChange={handleChange}
                                                    className="form-control font-weight-bold"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        {/* SEPARADOR / VÍNCULO */}
                                        <div className="col-md-2 d-flex align-items-center justify-content-center pt-4">
                                            <i className={`fas fa-link ${product.usar_mismo_precio ? 'text-primary' : 'text-muted'}`} style={{ fontSize: '1.2rem' }}></i>
                                        </div>
                                        

                                        {/* PRECIO WEB */}
                                        <div className="form-group col-md-5 mb-0">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <label className={`font-weight-bold mb-0 ${product.venta_web ? 'text-info' : 'text-muted'}`}>
                                                    Precio Web
                                                </label>
                                                {product.venta_web && (
                                                    <div className="custom-control custom-switch">
                                                        <input
                                                            type="checkbox"
                                                            className="custom-control-input"
                                                            id="usarMismoPrecio"
                                                            name="usar_mismo_precio"
                                                            checked={product.usar_mismo_precio}
                                                            onChange={handleChange}
                                                        />
                                                        <label className="custom-control-label small text-muted" htmlFor="usarMismoPrecio">Mismo que local</label>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="input-group mt-1">
                                                <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                                                <input
                                                    type="text"
                                                    name="precio_web"
                                                    value={product.precio_web}
                                                    onChange={handleChange}
                                                    className={`form-control font-weight-bold ${product.usar_mismo_precio ? 'bg-white' : ''}`}
                                                    disabled={!product.venta_web || product.usar_mismo_precio}
                                                    readOnly={product.usar_mismo_precio}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {/* SWITCH VISIBILIDAD TPV (CAJA) */}
<div className={`form-group mb-2 p-2 rounded border d-flex align-items-center justify-content-between ${product.mostrar_tpv ? 'bg-primary text-white shadow-sm' : 'bg-white'}`} style={{ transition: '0.3s' }}>
    <div className="d-flex align-items-center">
        <i className={`fas fa-cash-register mr-3 fa-lg ${product.mostrar_tpv ? 'text-white' : 'text-muted'}`}></i>
        <div>
            <label className="form-check-label font-weight-bold m-0" htmlFor="mostrarTpv" style={{ cursor: 'pointer' }}>
                Habilitar en Caja (TPV)
            </label>
            <p className="m-0 small" style={{ opacity: 0.8 }}>Permite vender este item en el local</p>
        </div>
    </div>
    <div className="custom-control custom-switch custom-switch-lg">
        <input
            type="checkbox"
            className="custom-control-input"
            id="mostrarTpv"
            name="mostrar_tpv" // Importante: coincide con el estado
            checked={product.mostrar_tpv}
            onChange={handleChange}
        />
        <label className="custom-control-label" htmlFor="mostrarTpv"></label>
    </div>
</div>
                                    {/* BOTÓN VENTA WEB MODERNO */}
                                    <div className={`form-group mb-3 p-2 rounded border d-flex align-items-center justify-content-between ${product.venta_web ? 'bg-info text-white shadow-sm' : 'bg-white'}`} style={{ transition: '0.3s' }}>
                                        <div className="d-flex align-items-center">
                                            <i className={`fas fa-globe mr-3 fa-lg ${product.venta_web ? 'text-white' : 'text-muted'}`}></i>
                                            <div>
                                                <label className="form-check-label font-weight-bold m-0" htmlFor="ventaWeb" style={{ cursor: 'pointer' }}>
                                                    Habilitar Venta Online
                                                </label>
                                                <p className="m-0 small" style={{ opacity: 0.8 }}>Muestra este producto en tu menú web</p>
                                            </div>
                                        </div>
                                        <div className="custom-control custom-switch custom-switch-lg">
                                            <input
                                                type="checkbox"
                                                className="custom-control-input"
                                                id="ventaWeb"
                                                name="venta_web"
                                                checked={product.venta_web}
                                                onChange={handleChange}
                                            />
                                            <label className="custom-control-label" htmlFor="ventaWeb"></label>
                                        </div>
                                    </div>

                                    {/* DESCRIPCIÓN */}
                                    <div className="form-group">
                                        <label className="font-weight-bold">Descripción Corta</label>
                                        <textarea name="description" value={product.description} onChange={handleChange} className="form-control" rows="2" />
                                    </div>



                                    {/* SWITCH VENTA POR PESO MODERNO */}
                                    <div className={`form-group mb-3 p-2 rounded border d-flex align-items-center justify-content-between ${product.venta_por_peso ? 'bg-info text-white shadow-sm' : 'bg-white'}`} style={{ transition: '0.3s' }}>
                                        <div className="d-flex align-items-center">
                                            <i className={`fas fa-weight mr-3 fa-lg ${product.venta_por_peso ? 'text-white' : 'text-muted'}`}></i>
                                            <div>
                                                <label className="form-check-label font-weight-bold m-0" htmlFor="ventaPorPeso" style={{ cursor: 'pointer' }}>
                                                    Venta por Peso / Granel
                                                </label>
                                                <p className="m-0 small" style={{ opacity: 0.8 }}>Permite venta con decimales en TPV</p>
                                            </div>
                                        </div>
                                        <div className="custom-control custom-switch custom-switch-lg">
                                            <input
                                                type="checkbox"
                                                className="custom-control-input"
                                                id="ventaPorPeso"
                                                name="venta_por_peso"
                                                checked={product.venta_por_peso}
                                                onChange={handleChange}
                                            />
                                            <label className="custom-control-label" htmlFor="ventaPorPeso"></label>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label className="font-weight-bold">Subir Imagen</label>
                                        <input type="file" name="image_file" onChange={handleFileChange} className="form-control-file" accept="image/*" />
                                        {(product.img || selectedFile) && (
                                            <div className="mt-2">
                                                <img src={selectedFile ? URL.createObjectURL(selectedFile) : config.img_url + product.img} alt="Preview" style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'cover' }} />
                                            </div>
                                        )}
                                    </div>

                                    <h6 className="font-weight-bold mt-4 mb-3 border-bottom pb-2 text-info">POS e Inventario</h6>

                                    <div className="form-row">
                                        <div className="form-group col-md-6">
                                            <label className="font-weight-bold text-muted small">Código de Barras</label>
                                            <input type="text" name="codigo_barras" value={product.codigo_barras} onChange={handleChange} className="form-control form-control-sm" placeholder="Opcional" />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label className="font-weight-bold text-muted small">Costo Ref. (CLP)</label>
                                            <input type="number" name="costo" value={product.costo} onChange={handleChange} className="form-control form-control-sm" min="0" />
                                        </div>
                                    </div>

                                    <div className="form-group form-check mb-3 bg-light p-2 rounded">
                                        <input
                                            type="checkbox" className="form-check-input ml-1" id="ctrlStock" name="controlar_stock"
                                            checked={product.controlar_stock} onChange={handleChange}
                                            disabled={product.receta && product.receta.length > 0}
                                        />
                                        <label className="form-check-label font-weight-bold ml-4" htmlFor="ctrlStock">Controlar Inventario Activo</label>
                                        {product.receta && product.receta.length > 0 && (
                                            <div className="small text-muted ml-4 mt-1">
                                                Este producto tiene receta: su disponibilidad depende del stock de los insumos, no de un stock propio.
                                            </div>
                                        )}
                                    </div>

                                    {product.controlar_stock && (
                                        <div className="form-row border p-2 rounded bg-white">
                                            <div className="form-group col-md-6 mb-2">
                                                <label className="font-weight-bold text-muted small">Stock Actual</label>
                                                <input type="number" name="stock" value={product.stock} onChange={handleChange} className="form-control form-control-sm border-success" step="0.001" />
                                            </div>
                                            <div className="form-group col-md-6 mb-2">
                                                <label className="font-weight-bold text-muted small">Alerta Mínimo</label>
                                                <input type="number" name="stock_minimo" value={product.stock_minimo} onChange={handleChange} className="form-control form-control-sm" step="0.001" />
                                            </div>
                                        </div>
                                    )}

                                    {/* GESTOR DE RECETA */}
                                    <RecipeManager
                                        recipe={product.receta}
                                        setRecipe={(newRecipe) => setProduct(prev => ({
                                            ...prev,
                                            receta: newRecipe,
                                            // Un producto con receta no debe llevar stock propio: su
                                            // disponibilidad la dan los insumos, no un contador aparte
                                            // que nadie repone (ver caso "Lomo Saltado" con stock -1).
                                            controlar_stock: newRecipe.length > 0 ? false : prev.controlar_stock
                                        }))}
                                        availableProducts={insumosDisponibles}
                                    />

                                </form>
                            </div>

                            {/* COLUMNA DERECHA: VARIANTES */}
                            <div className="col-md-7">
                                <h6 className="font-weight-bold mb-3 border-bottom pb-2 text-primary">Variantes y Adiciones</h6>
                                <VariantManager
                                    productVariants={product.variantes}
                                    setProductVariants={handleVariantChange}
                                    openAddGroupModal={() => setIsGroupModalOpen(true)}
                                    setIsTemplateModalOpen={setIsTemplateModalOpen}
                                    onSaveAsTemplate={handleSaveAsTemplate}
                                    showConfirm={showConfirm}
                                />
                            </div>
                        </div>
                    </div>

                    {/* FOOTER */}
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
                        <button type="submit" form="product-base-form" className="btn btn-primary font-weight-bold" disabled={saving}>
                            {saving ? <SpinnerIcon /> : <AddIcon />}
                            {saving ? 'Guardando...' : (productToEdit ? 'Guardar Cambios' : 'Crear Producto')}
                        </button>
                    </div>
                </div>
            </div>
            <AddGroupModal
                isVisible={isGroupModalOpen}
                onClose={() => setIsGroupModalOpen(false)}
                onSaveGroup={handleAddGroupToProduct}
            />

            {/* NUEVO MODAL DE PLANTILLAS */}
            <TemplateManagerModal
                isVisible={isTemplateModalOpen}
                onClose={() => setIsTemplateModalOpen(false)}
                onApplyTemplate={handleApplyTemplate}
            />

            {/* CONFIRM MODAL UNIFICADO */}
            <ConfirmModal
                isVisible={confirmState.isVisible}
                title={confirmState.title}
                message={confirmState.message}
                type={confirmState.type}
                inputLabel={confirmState.inputLabel}
                inputValue={confirmState.inputValue}
                onConfirm={confirmState.onConfirm}
                onCancel={confirmState.onCancel}
            />
        </div>

    );
};

export default ProductFormModal;